import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { JOURNEYS, IMAGES } from "../content";
import { useLightbox } from "./Lightbox";
import { EASE, Reveal } from "../motion";
import { Section, Wrap, SectionHead } from "./Section";

export default function Journeys() {
  const [active, setActive] = useState(JOURNEYS[0]);
  const openLightbox = useLightbox();

  return (
    <Section id="journeys" theme="neutral" className="py-16 sm:py-24">
      <Wrap>
        <SectionHead
          eyebrow="Example customer journeys"
          title="Three enquiry routes, one reassuring pattern."
          intro="Each audience travels differently, but every journey ends the same way: a short, respectful enquiry, never a wall of forms."
        />

        <Reveal className="mt-9">
          <div data-testid="journey-tabs" className="flex flex-wrap gap-2" role="tablist" aria-label="Customer journeys">
            {JOURNEYS.map((j) => (
              <button
                key={j.id}
                data-testid={`journey-tab-${j.id}`}
                role="tab"
                aria-selected={active.id === j.id}
                onClick={() => setActive(j)}
                className={`text-[11px] font-bold uppercase tracking-[0.16em] px-6 py-3 rounded-full border transition-[background-color,color,border-color] duration-300 cursor-pointer ${
                  active.id === j.id
                    ? "bg-[#242424] text-[#F5F3EE] border-[#242424]"
                    : "border-[#242424]/20 text-[#242424]/65 hover:border-[#242424]/50"
                }`}
              >
                {j.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            data-testid="journey-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-9 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start"
          >
            <div className="lg:col-span-7">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#B66E50]">{active.tone}</p>
              <ol className="mt-8 flex flex-col sm:flex-row sm:items-stretch gap-0">
                {active.steps.map((s, i) => (
                  <motion.li
                    key={s}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE, delay: 0.15 + i * 0.12 }}
                    className="flex sm:flex-col items-start sm:items-center gap-4 sm:gap-0 sm:flex-1 relative"
                  >
                    <span className="flex sm:flex-col items-center gap-4 sm:gap-3">
                      <span className="w-11 h-11 rounded-full bg-[#F6F0E6] border border-[#6F806E]/35 flex items-center justify-center font-serif italic text-lg text-[#4D5D4C] shrink-0 z-10">
                        {i + 1}
                      </span>
                      {i < active.steps.length - 1 && (
                        <span className="hidden sm:block absolute top-[22px] left-1/2 w-full h-px bg-[#6F806E]/25" aria-hidden="true" />
                      )}
                    </span>
                    <p className="text-sm font-medium text-[#242424]/80 sm:text-center sm:mt-4 sm:px-2 leading-snug pt-2.5 sm:pt-0">{s}</p>
                  </motion.li>
                ))}
              </ol>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="lg:col-span-5"
            >
              <button
                data-testid="journey-image"
                onClick={() => openLightbox(active.imageKey)}
                aria-label={`View photograph: ${active.imageAlt}`}
                className="relative block w-full rounded-3xl overflow-hidden h-52 sm:h-60 group cursor-pointer"
              >
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active.id}
                    src={IMAGES[active.imageKey]}
                    alt={active.imageAlt}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </AnimatePresence>
                <span className="absolute inset-0 bg-gradient-to-t from-[#26342E]/55 to-transparent" aria-hidden="true" />
                <span className="absolute bottom-4 left-5 text-[9px] font-bold uppercase tracking-[0.22em] text-[#F6F0E6]/90">
                  {active.label} · tap to view in full
                </span>
              </button>
              <div className="bg-[#F6F0E6] border-l-2 border-[#B66E50] p-8 sm:p-10 mt-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#4D5D4C]">Why it works</p>
                <p className="font-serif text-xl sm:text-2xl font-light leading-relaxed text-[#26342E] mt-4">{active.note}</p>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Wrap>
    </Section>
  );
}
